import { isAfter, isBefore, isWithinInterval } from 'date-fns';
import * as XLJS from 'exceljs';
import { nanoid } from 'nanoid';

const ARRAY_FIELD = [
  'secondary_unit_name',
  'secondary_unit_qty',
  'tags',
  'phones',
  'contact_person',
];
const BLOCK_FIELD = 'is_blocked';

function isValidDateRange(
  startDate: string | undefined | null,
  endDate: string | undefined | null
): boolean {
  try {
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    if (start && end) {
      if (
        isWithinInterval(new Date(), {
          start,
          end,
        })
      )
        return true;
    } else {
      if (start) {
        if (isAfter(new Date(), start)) return true;
      }
      if (end) {
        if (isBefore(new Date(), end)) return true;
      }
      // empty means no range thus within range
      if (!start && !end) return true;
    }
    return false;
  } catch (e) {
    return false;
  }
}

function transformToArray(input: any | null): string[] {
  if (!input || input == '') {
    return [];
  }

  input = input.toString().replace(/^\[|\]$/g, ''); // remove []
  const cleaned: string[] = input
    .trim()
    .split(',')
    .map((i: any) => i.trim());

  return cleaned;
}

function fillSparseArr(arr: any[]) {
  for (let i = 0; i < arr.length; ++i) {
    if (!(i in arr)) {
      // explicit check for missing sparse value
      arr[i] = null;
    }
  }
}

function transformRecord(
  row: any,
  fieldMap: any,
  columnFieldIdx: any
): Map<string, any> {
  const result = new Map<string, any>();
  fillSparseArr(row);
  Object.keys(fieldMap).map(function (value: string) {
    let actualValue: any = parseCellValue(row[columnFieldIdx[value]]);
    const key = fieldMap[value.toLocaleLowerCase()];
    if (ARRAY_FIELD.includes(key)) {
      actualValue = transformToArray(actualValue || null);
    } else if (BLOCK_FIELD === key) {
      const acceptedTrueValues = ['true', 'yes', 'block', 'y', 't'];
      if (typeof actualValue != 'boolean') {
        actualValue = acceptedTrueValues.includes(actualValue?.toLowerCase())
          ? true
          : false;
      }
    }

    result.set(key, actualValue);
  });

  // DataGrid id
  result.set('id', nanoid());
  return result;
}

function validateHeader(sheet: any, _fieldMap: any, _isTlf: boolean) {
  const head = sheet.getRow(1);
  if (!head) {
    throw new Error('file must not be empty');
  }
}

async function validateRecord(
  row: any,
  fieldMap: any,
  schema: any,
  columnFieldIdx: any
): Promise<{
  error: any;
  data: any;
}> {
  const transformed = Object.fromEntries(
    transformRecord(row, fieldMap, columnFieldIdx)
  );
  try {
    const validated = await schema.validate(transformed);
    return { error: null, data: validated };
  } catch (e) {
    return { error: e, data: transformed };
  }
}

export async function parseAndValidate(
  sheet: XLJS.Worksheet,
  fieldMap: any,
  schema: any,
  uniqueKey: string[],
  isTlf: boolean,
  bail = false,
  // This function mean to be generic, however there are cases where TLF in import product
  // Has start and end date as a unique identifier
  // This flag is to control that flow, so it doesn't affect other functionality
  isUseStartEndDate = false
): Promise<{ hasError: boolean; data: any[] }> {
  // ignore header
  if (sheet.rowCount <= 1) {
    throw new Error('file must not be empty');
  }

  validateHeader(sheet, fieldMap, isTlf);

  let i = 1;
  // hasError is used to flag if an error happen in any rows
  let hasError = false;
  // skip header
  const rows = sheet.getRows(2, sheet.rowCount);
  const resultRows: any[] = [];
  // a map of a unique key and the index of the item
  const uniqueItemMap: Map<string, number> = new Map<string, number>();
  // after the validation, there might be duplicates that we can eliminate, mainly for tlf
  const itemsToDelete: number[] = [];

  if (!rows) {
    throw new Error('file must not be empty');
  }

  const columnFieldIdxMap = Object.keys(fieldMap).reduce((accum, curr) => {
    const head = sheet.getRow(1);

    // Find the index of the header value
    const index = head.values.findIndex((x) =>
      x
        ? x.toLocaleLowerCase().trim() === curr.toLocaleLowerCase().trim()
        : false
    );

    // If index is -1 (not found), set it to 0, otherwise use the found index
    const columnIndex = index === -1 ? 0 : index;

    // Merge the current key-value pair into the accumulated object
    return {
      ...accum,
      [curr]: columnIndex,
    };
  }, {});

  const handleValidationError = (
    error: null | { message: string },
    data: any
  ) => {
    if (bail) throw error;
    hasError = true;
    data['errors'] = error?.message || null;
  };

  const handleUniqueKeyValidation = async (
    data: any,
    idx: number,
    originalRow: string[]
  ) => {
    const key = createUniqueKey(uniqueKey, data);
    const existingIdx = uniqueItemMap.get(key);
    if (existingIdx != undefined && existingIdx != null) {
      const firstLineProductKey = rows[existingIdx]?.values;

      // at this point can't track the reference anymore
      // there will be error stating circular json
      // caused by object that has circular reference to other object
      // so that JSON.parse couldn't handle it
      // Filter out values that don't exist in fieldMap before comparing
      const filteredFirstLine = firstLineProductKey?.filter((_: any, i: number) =>
        Object.values(columnFieldIdxMap).includes(i)
      );
      const filteredOriginalRow = originalRow?.filter((_: any, i: number) =>
        Object.values(columnFieldIdxMap).includes(i)
      );

      if (
        JSON.stringify(filteredFirstLine)?.toLowerCase() ===
        JSON.stringify(filteredOriginalRow)?.toLowerCase()
      ) {
        // Skip if filtered values match
        return 'skipped';
      }

      await handleDuplicateValidation(data, idx, existingIdx, key);
    } else {
      uniqueItemMap.set(key, idx);
    }
  };

  const handleDuplicateValidation = async (
    data: any,
    idx: number,
    existingIdx: number,
    key: string
  ) => {
    if (isUseStartEndDate) {
      // at the moment only tlf use start and end date
      const { data: duplicateData } = await validateRecord(
        rows[existingIdx].values,
        fieldMap,
        schema,
        columnFieldIdxMap
      );
      const isDuplicateValid = isValidDateRange(
        duplicateData['start_date'],
        duplicateData['end_date']
      );

      // current will always be valid date, else it will be removed and skipped already
      if (!isDuplicateValid) {
        itemsToDelete.push(existingIdx);
        uniqueItemMap.set(key, idx);
      }
      if (isDuplicateValid) {
        // if one of them is non empty and is within range, choose that
        if (data['start_date'] || data['end_date']) {
          // delete duplicate
          itemsToDelete.push(existingIdx);
          uniqueItemMap.set(key, idx);
        } else if (duplicateData['start_date'] || duplicateData['end_date']) {
          // delete current
          itemsToDelete.push(idx);
        } else {
          // check whether their units are different, if unit the only difference pick the first
          if (data['base_unit_name'] !== duplicateData['base_unit_name']) {
            itemsToDelete.push(idx);
          } else {
            // both are within range, both empty, mark both as duplicate and flag it out
            data['errors'] = 'DUPLICATE_LINE_ITEM';
            resultRows[existingIdx]['errors'] = 'DUPLICATE_LINE_ITEM';
          }
        }
      }
    } else {
      hasError = true;
      data['errors'] = 'DUPLICATE_LINE_ITEM';
    }
  };

  const processRow = async (row: any, idx: number) => {
    const { error, data } = await validateRecord(
      row.values,
      fieldMap,
      schema,
      columnFieldIdxMap
    );
    data['line_no'] = i + 1;

    if (error) {
      handleValidationError(error, data);
    }

    if (isUseStartEndDate) {
      // check for date
      const isCurrValid = isValidDateRange(
        data['start_date'],
        data['end_date']
      );
      if (!isCurrValid) {
        itemsToDelete.push(idx);
        resultRows.push(data);
        i++;
        // skip the uniqueness check because invalid rows will be just deleted
        return;
      }
    }

    if (uniqueKey.length > 0) {
      const h = await handleUniqueKeyValidation(data, idx, row.values);
      if (h == 'skipped') {
        return;
      }
    }

    i++;
    resultRows.push(data);
  };

  const processRows = async () => {
    for (let idx = 0; idx < rows.length; idx++) {
      const row = rows[idx];
      if (!row.values || !row.values.length) {
        i++;
        continue;
      }

      try {
        await processRow(row, idx);
      } catch (error) {
        hasError = true;
        console.error(`Error processing row ${i + 1}:`, error);
      }
    }
  };

  try {
    await processRows();
    return {
      hasError,
      data: resultRows.filter((_row, idx) => !itemsToDelete.includes(idx)),
    };
  } catch (e: any) {
    throw new Error(`Error in row number ${i}: ${e.message}`);
  }
}

export function parseCellValue(value: XLJS.CellValue) {
  // | null | number | string | boolean | Date | undefined
  // | CellErrorValue
  // | CellRichTextValue | CellHyperlinkValue
  // | CellFormulaValue | CellSharedFormulaValue;

  const genericValue = value as any;
  if (genericValue === null || genericValue == undefined) {
    return null;
  }
  // CellErrorValue
  if (genericValue.error) {
    return genericValue.error?.trim()?.replace(/\n/g, '') || null;
  }

  // CellRichTextValue
  if (genericValue.richText) {
    return genericValue.richText[0].text?.trim()?.replace(/\n/g, '') || null;
  }

  // CellHyperlinkValue
  if (genericValue.hyperlink) {
    if (genericValue.text?.richText) {
      return genericValue.text.richText[0]?.text?.trim()?.replace(/\n/g, '') || null;
    }
    return genericValue.text?.trim()?.replace(/\n/g, '') || null;
  }

  // CellFormulaValue , CellSharedFormulaValue
  if (genericValue.formula || genericValue.sharedFormula) {
    const { result } = genericValue;
    if (result?.error) {
      return result.error?.trim()?.replace(/\n/g, '') || null;
    }

    // This one coming from TLF file, which I don't know why it has this formula
    const f = genericValue.formula;
    if (f == 'FALSE()') {
      return 'FALSE';
    }

    if (f == 'TRUE()') {
      return 'TRUE';
    }

    return typeof result === 'string' ? result.trim().replace(/\n/g, '') : result;
  }

  return typeof genericValue === 'string' ? genericValue.trim().replace(/\n/g, '') : genericValue;
}

function createUniqueKey(uniqueKey: string[], data: any) {
  let result = '';
  for (const key of uniqueKey) {
    result = `${result}__${data[key]}`;
  }

  return result;
}

// Function to convert Excel column letters to numbers (e.g., A -> 1, B -> 2, Z -> 26, AA -> 27)
function columnLetterToNumber(column: string): number {
  let result = 0;
  for (let i = 0; i < column.length; i++) {
    result = result * 26 + (column.charCodeAt(i) - 'A'.charCodeAt(0) + 1);
  }
  return result;
}

// Function to check if a cell is within a given merged range
function isCellInRange(cellAddress: string, range: string): boolean {
  // Split the range into top-left and bottom-right cells
  const [startCell, endCell] = range.split(':');

  // Extract column letters and row numbers for the start and end cells
  const startColumn = startCell.match(/[A-Z]+/i)![0]; // Extract letters (column)
  const startRow = parseInt(startCell.match(/\d+/)![0]); // Extract numbers (row)

  const endColumn = endCell.match(/[A-Z]+/i)![0]; // Extract letters (column)
  const endRow = parseInt(endCell.match(/\d+/)![0]); // Extract numbers (row)

  // Convert the column letters to numbers for easier comparison
  const startColNum = columnLetterToNumber(startColumn);
  const endColNum = columnLetterToNumber(endColumn);

  // Extract the column and row from the cellAddress
  const cellColumn = cellAddress.match(/[A-Z]+/i)![0];
  const cellRow = parseInt(cellAddress.match(/\d+/)![0]);

  const cellColNum = columnLetterToNumber(cellColumn);

  // Check if the cell is within the bounds of the range
  return (
    cellColNum >= startColNum &&
    cellColNum <= endColNum &&
    cellRow >= startRow &&
    cellRow <= endRow
  );
}

export function convertExcelWorksheetToCsv(worksheet: XLJS.Worksheet) {
  // Initialize a CSV string
  let csvString = '';

  // Get all merged cells in the worksheet
  const mergedRanges = worksheet.model.merges;
  // console.log('mergedRanges:', mergedRanges)
  // Iterate over each row in the worksheet
  worksheet.eachRow({ includeEmpty: true }, (row) => {
    const rowValues = row.values.map(
      (cell: XLJS.CellValue, colNumber: number) => {
        // Get the address of the current cell
        const cellAddress = row.getCell(colNumber).address;

        // Find the merged range that includes this cell
        const mergeRange = mergedRanges.find((range) =>
          isCellInRange(cellAddress, range)
        );

        // if (rowNumber === 13 || rowNumber === 14) console.log(`row ${rowNumber} cell`, cell, mergeRange, cellAddress,)

        // If the cell is part of a merged range and not the top-left cell, skip it
        if (mergeRange && cellAddress !== mergeRange.split(':')[0]) {
          return ','; // Skip non-top-left merged cells
        }
        if (cell === null || cell === undefined) return ',';

        // Handle cells with text property
        if (typeof cell === 'object' && cell && (cell as XLJS.RichText).text) {
          const text = (cell as XLJS.RichText).text;
          // escape the comma
          return text.includes(',') ? `"${text}"` : text;
        }

        if (typeof cell === 'string')
          return cell.includes(',') ? `"${cell}"` : cell;
        return cell;
      }
    );
    csvString += rowValues.join(',') + '\n';
  });

  return csvString.trim();
}

async function cloneWorksheet(
  sourceWorksheet: XLJS.Worksheet,
  targetWorksheet: XLJS.Worksheet
) {
  // Copy all rows and cells
  sourceWorksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
    const targetRow = targetWorksheet.getRow(rowNumber);
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      const targetCell = targetRow.getCell(colNumber);

      // Copy cell value (including formulas)
      targetCell.value = cell.value;

      // Copy cell styles (font, alignment, border, fill, etc.)
      if (cell.style) {
        targetCell.style.border = cell.style.border;
      }
    });
    // Copy row height if specified
    if (row.height) {
      targetRow.height = row.height;
    }
  });

  // Copy merged cells
  sourceWorksheet.model.merges?.forEach((mergeRange) => {
    targetWorksheet.mergeCells(mergeRange);
  });

  // Copy column widths
  sourceWorksheet.columns?.forEach((column, colIndex) => {
    if (column.width) {
      targetWorksheet.getColumn(colIndex + 1).width = column.width;
    }
  });

  // Copy row heights
  sourceWorksheet.eachRow({ includeEmpty: true }, (row, rowIndex) => {
    const targetRow = targetWorksheet.getRow(rowIndex);
    if (row.height) {
      targetRow.height = row.height;
    }
  });

  // Copy sheet view options (e.g., frozen panes, etc.)
  targetWorksheet.views = [...sourceWorksheet.views];
}

export async function splitExcelFileToWorksheets(file: File) {
  // Step 1: Create a new ExcelJS workbook
  const workbook = new XLJS.Workbook();

  // Step 2: Read the file as ArrayBuffer
  const arrayBuffer = await file.arrayBuffer();

  // Step 3: Load the workbook from the ArrayBuffer
  await workbook.xlsx.load(arrayBuffer);

  // Prepare an array to hold the generated File objects
  const fileObjects = [];

  for (let i = 0; i < workbook.worksheets.length; i++) {
    const worksheet = workbook.worksheets[i];

    // Create a new workbook for each worksheet
    const newWorkbook = new XLJS.Workbook();
    const newWorksheet = newWorkbook.addWorksheet(worksheet.name);

    cloneWorksheet(worksheet, newWorksheet);
    // Generate an Excel file Blob for the worksheet
    const buffer = await newWorkbook.xlsx.writeBuffer();

    // Convert the buffer to a Blob
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    const fileObject = new File([blob], `${worksheet.name}.xlsx`, {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });

    // Add the File object to the array
    fileObjects.push(fileObject);
  }

  return fileObjects;
}
